// BookingInquiryCard.tsx
"use client";

import * as React from "react";
import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
} from "@mui/material";
import { useI18n } from "i18n/I18nProvider";

type Props = {
  propertyTitle: string;
  propertyId?: number | null;
  propertySlug?: string | null;
  maxGuests?: number | null;
  locale?: string;
  propertyUrl?: string;
};

function isoToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function BookingInquiryCard({
  propertyTitle,
  propertyId = null,
  propertySlug = null,
  maxGuests = null,
  locale = "en",
  propertyUrl,
}: Props) {
  const { t } = useI18n();
  const API_BASE = process.env.REACT_APP_API_BASE;

  const [fullName, setFullName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [guests, setGuests] = React.useState<number>(
    maxGuests ? Math.min(2, maxGuests) : 2
  );
  const [checkIn, setCheckIn] = React.useState("");
  const [checkOut, setCheckOut] = React.useState("");
  const [arrivalTime, setArrivalTime] = React.useState("");
  const [pets, setPets] = React.useState(false);
  const [message, setMessage] = React.useState("");

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [okMsg, setOkMsg] = React.useState<string | null>(null);

  const canSubmit = React.useMemo(() => {
    if (!fullName || !email || !phone || !checkIn || !checkOut) return false;
    return new Date(checkOut) > new Date(checkIn);
  }, [fullName, email, phone, checkIn, checkOut]);

  const submit = async () => {
    setError(null);
    setOkMsg(null);

    if (!canSubmit) {
      setError(t("booking.errorRequired"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/booking-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_id: propertyId,
          property_title: propertyTitle,
          property_slug: propertySlug,
          property_url: propertyUrl,
          check_in: checkIn,
          check_out: checkOut,
          guests_total: guests,
          arrival_time: arrivalTime || null,
          pets,
          full_name: fullName,
          email,
          phone,
          message: message || null,
          locale,

          // ✅ ΝΕΟ: έλεγχος αποθήκευσης
          save_to_db: true,
        }),
      });

      if (!res.ok) throw new Error();
      setOkMsg(t("booking.success"));
    } catch {
      setError(t("booking.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        p: { xs: 2, md: 2.25 },
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={1.4}>
        <Typography variant="h6" fontWeight={900}>
          {t("booking.title")}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {t("booking.subtitle")}
        </Typography>

        <Divider />

        {error && <Alert severity="error">{error}</Alert>}
        {okMsg && <Alert severity="success">{okMsg}</Alert>}

        <TextField
          label={t("booking.field.property")}
          value={propertyTitle}
          fullWidth
          InputProps={{ readOnly: true }}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
          <TextField
            label={t("booking.field.checkIn")}
            type="date"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            inputProps={{ min: isoToday() }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label={t("booking.field.checkOut")}
            type="date"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
            inputProps={{ min: checkIn || isoToday() }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>

        <TextField
          label={t("booking.field.guests")}
          type="number"
          value={guests}
          onChange={(e) => setGuests(Number(e.target.value))}
          helperText={
            maxGuests ? t("booking.helper.maxGuests", { n: maxGuests }) : undefined
          }
          inputProps={{ min: 1, max: maxGuests ?? 50 }}
          fullWidth
        />

        <TextField
          label={t("booking.field.arrivalTime")}
          value={arrivalTime}
          onChange={(e) => setArrivalTime(e.target.value)}
          placeholder={t("booking.placeholder.arrivalTime")}
          fullWidth
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
          <TextField
            label={t("booking.field.fullName")}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("booking.field.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
        </Stack>

        <TextField
          label={t("booking.field.phone")}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
        />

        <TextField
          label={t("booking.field.pets")}
          select
          SelectProps={{ native: true }}
          value={pets ? "yes" : "no"}
          onChange={(e) => setPets(e.target.value === "yes")}
          fullWidth
        >
          <option value="no">{t("booking.pets.no")}</option>
          <option value="yes">{t("booking.pets.yes")}</option>
        </TextField>

        <TextField
          label={t("booking.field.message")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("booking.placeholder.message")}
          multiline
          minRows={3}
          fullWidth
        />

        <Button
          variant="contained"
          size="large"
          onClick={submit}
          disabled={!canSubmit || loading}
          sx={{ fontWeight: 900 }}
        >
          {loading ? t("booking.submitting") : t("booking.submit")}
        </Button>

        <Typography variant="caption" color="text.secondary">
          {t("booking.requiredNote")}
        </Typography>
      </Stack>
    </Paper>
  );
}
